import React, { useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  Save,
  CreditCard
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollApi";
import * as portalApi from "../../api/payroll/payrollPortalApi";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select
} from "../../components/ui";
import {
  cn
} from "../../lib/cn";
import {
  useToast
} from "../../context/ToastContext";

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const STATUS_CHIP_STYLES = {
  PAID: "bg-success-500 text-white border-success-500",
  FAILED: "bg-danger-500 text-white border-danger-500",
  PENDING: "bg-warning-500 text-white border-warning-500",
};

const MODE_OPTIONS = [
  { value: "BANK_TRANSFER", label: "Bank transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CASH", label: "Cash" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "ONLINE", label: "Online" },
];

export default function PayslipPaymentForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    paymentStatus: "PAID",
    paymentMode: "BANK_TRANSFER",
    paymentReference: "",
    paymentDate: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const quickSetStatus = (status) => {
    setForm((prev) => ({ ...prev, paymentStatus: status }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.updatePayslipPayment(id, {
        ...form,
        paymentDate: form.paymentDate ? new Date(form.paymentDate) : null,
      });
      toast.success(res.message || "Payment updated");
      setTimeout(() => navigate(-1), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <CreditCard className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Update Payment
        </h1>
      </div>

      
      

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Quick status chips — select and done */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900 dark:text-neutral-100">Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => quickSetStatus(s.value)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200",
                      form.paymentStatus === s.value
                        ? STATUS_CHIP_STYLES[s.value]
                        : "border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400"
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <Select
              label="Mode"
              options={MODE_OPTIONS}
              value={form.paymentMode}
              onChange={(val) => setForm((prev) => ({ ...prev, paymentMode: val }))}
            />

            <Input
              label={
                <>
                  Reference <span className="font-normal text-neutral-400 dark:text-neutral-500">(optional)</span>
                </>
              }
              name="paymentReference"
              value={form.paymentReference}
              onChange={handleChange}
              placeholder="Txn ID / cheque no."
            />

            <Input
              type="date"
              label="Payment date"
              name="paymentDate"
              value={form.paymentDate}
              onChange={handleChange}
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" variant="primary" isLoading={loading} leftIcon={<Save className="h-4 w-4" />}>
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}